import type { PluginOpKind } from '../../src/shared/wireContract';
import type { SystemUpdateStatus } from './types';
import type { UpdateDisplayState } from './updateStatus';
import { interpolate } from './i18n';
import type { useTranslation } from './i18n';

export interface UpdateDockOperation {
  kind: 'update';
  id: string;
  name: string | null;
  op: PluginOpKind | null;
  status: 'running' | 'succeeded' | 'failed';
  percent: null;
  error: string | null;
  updatesHeld?: true;
  updatedAt: string;
  acknowledgedAt: string | null;
  state: UpdateDisplayState;
  requestId: string;
}

export function updateOperationTitle(operation: UpdateDockOperation, copy: ReturnType<typeof useTranslation>['t']['operationDock']): string {
  return operation.name === null ? copy.updateCore
    : interpolate((operation.op === 'install' ? copy.installPlugin : operation.op === 'repair' ? copy.repairPlugin : copy.updatePlugin), { name: operation.name });
}

/** Read-only projection of the existing queue, recovery journal and retained request verdicts. */
export function updateDockOperations(status: SystemUpdateStatus | undefined, userId: number): UpdateDockOperation[] {
  if (!status) return [];
  const items = new Map<string, UpdateDockOperation>();
  const put = (request: { requestId: string; by: number | null; uiInitiated?: true; op?: PluginOpKind; updatesHeld?: true }, name: string | null,
    state: UpdateDisplayState, at: number, error: string | null, acknowledgedAt: number | null) => {
    if (request.by !== userId || !request.uiInitiated) return;
    const succeeded = state === 'committed' || state === 'applied' || state === 'noUpdate';
    const failed = ['rolledBack', 'blocked', 'needsOperator', 'needs-consent', 'not-licensed', 'superseded', 'failed', 'skipped'].includes(state);
    items.set(request.requestId, {
      kind: 'update', id: `update:${request.requestId}`, name, op: request.op ?? null, requestId: request.requestId, state,
      status: succeeded ? 'succeeded' : failed ? 'failed' : 'running', percent: null,
      ...(request.updatesHeld ? { updatesHeld: true } : {}),
      error, updatedAt: new Date(at).toISOString(),
      acknowledgedAt: acknowledgedAt === null ? null : new Date(acknowledgedAt).toISOString(),
    });
  };
  for (const op of status.report?.operations ?? []) put(op, op.name, op.state, op.at, op.error, op.acknowledgedAt);
  const journal = status.journal;
  if (journal) {
    const requests = [...journal.ops, ...(journal.coreRequest ? [{ ...journal.coreRequest, name: null }] : [])];
    for (const op of requests) {
      // needsOperator remains in the journal after the per-request report is published and acknowledged.
      if (items.has(op.requestId) && journal.state === 'needsOperator') continue;
      put(op, op.name, journal.state, journal.startedAt, null, null);
    }
  }
  for (const op of status.queue.plugins) put(op, op.name, 'queued', op.requestedAt, null, null);
  if (status.queue.core) put(status.queue.core, null, 'queued', status.queue.core.requestedAt, null, null);
  return [...items.values()].filter(op => op.acknowledgedAt === null).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}
